import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import { navigateToHelpSupport } from '../../utils/authHelpers';
import { 
  Play, 
  Book, 
  Filter, 
  Search, 
  ArrowLeft, 
  ArrowRight,
  Clock,
  Star,
  CheckCircle,
  Calendar,
  Package,
  Users,
  Settings,
  FileText,
  Code,
  MessageSquare,
  AlertTriangle
} from 'lucide-react';

const Tutorials = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [difficulty, setDifficulty] = useState('all');
  const [showAuthAlert, setShowAuthAlert] = useState(false);
  
  // Mock function to check if user is authenticated - replace with your actual auth check
  const isAuthenticated = () => {
    // Replace with your authentication check logic
    return false; // Assuming user is not logged in for demo
  };

  const handleHelpCenterClick = (e) => {
    e.preventDefault();
    navigateToHelpSupport(navigate);
  };

  const tutorials = [
    {
      id: 1,
      title: 'Beetrax\'a Başlangıç: Temel Özellikler',
      description: 'Beetrax platformunun temel özelliklerini ve kullanım alanlarını keşfedin.',
      category: 'getting-started',
      duration: 12,
      difficulty: 'beginner',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-1.jpg',
      author: 'Ahmet Yılmaz',
      views: 1850,
      completed: true,
      new: false
    },
    {
      id: 2,
      title: 'İlk Projenizi Oluşturun',
      description: 'Adım adım ilk projenizi oluşturmayı ve temel ayarları yapılandırmayı öğrenin.',
      category: 'getting-started',
      duration: 15,
      difficulty: 'beginner',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-2.jpg',
      author: 'Mehmet Kaya',
      views: 1248,
      completed: true,
      new: false
    },
    {
      id: 3,
      title: 'Görev Oluşturma ve Atama',
      description: 'Görevleri oluşturma, atama ve durumlarını takip etmeyi öğrenin.',
      category: 'task-management',
      duration: 18,
      difficulty: 'beginner',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-3.jpg',
      author: 'Zeynep Demir',
      views: 976,
      completed: false,
      new: false
    },
    {
      id: 4,
      title: 'Gelişmiş Görev Yönetimi',
      description: 'Tekrarlayan görevler, bağımlılıklar ve iş akışları ile görev yönetimini optimize edin.',
      category: 'task-management',
      duration: 22,
      difficulty: 'intermediate',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-4.jpg',
      author: 'Ahmet Yılmaz',
      views: 764,
      completed: false,
      new: true
    },
    {
      id: 5,
      title: 'Stok Katalog Yönetimi',
      description: 'Ürün kataloğu oluşturma ve stok bilgilerini yapılandırma.',
      category: 'stock-management',
      duration: 20,
      difficulty: 'beginner',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-5.jpg',
      author: 'Ayşe Yıldız',
      views: 1105,
      completed: false,
      new: false
    },
    {
      id: 6,
      title: 'Barkod ve QR Kod Entegrasyonu',
      description: 'Ürünlerinizi barkod ve QR kodlarla hızlıca takip etmeyi öğrenin.',
      category: 'stock-management',
      duration: 25,
      difficulty: 'intermediate',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-6.jpg',
      author: 'Emre Şahin',
      views: 892,
      completed: false,
      new: true
    },
    {
      id: 7,
      title: 'Kullanıcı Yönetimi ve İzinler',
      description: 'Kullanıcıları yönetmeyi, roller atamayı ve izinleri yapılandırmayı öğrenin.',
      category: 'user-management',
      duration: 16,
      difficulty: 'intermediate',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-7.jpg',
      author: 'Mehmet Kaya',
      views: 734,
      completed: false,
      new: false
    },
    {
      id: 8,
      title: 'Raporlar ve Grafikler',
      description: 'Özel raporlar oluşturmayı ve verileri görselleştirmeyi öğrenin.',
      category: 'reporting',
      duration: 28,
      difficulty: 'advanced',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-8.jpg',
      author: 'Zeynep Demir',
      views: 623,
      completed: false,
      new: true
    },
    {
      id: 9,
      title: 'API Kullanımı ve Entegrasyonlar',
      description: 'Beetrax API\'sini kullanarak diğer sistemlerle entegrasyon sağlamayı öğrenin.',
      category: 'api',
      duration: 35,
      difficulty: 'advanced',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-9.jpg',
      author: 'Emre Şahin',
      views: 512,
      completed: false,
      new: true
    },
    {
      id: 10,
      title: 'Tedarik Zinciri Yönetimi',
      description: 'Tedarikçileri yönetmeyi ve sipariş süreçlerini optimize etmeyi öğrenin.',
      category: 'stock-management',
      duration: 24,
      difficulty: 'intermediate',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-10.jpg',
      author: 'Ayşe Yıldız',
      views: 678,
      completed: false,
      new: false
    },
    {
      id: 11,
      title: 'İş Akışı Otomasyonu',
      description: 'Tekrarlayan görevleri otomatikleştirmeyi ve iş akışlarını yapılandırmayı öğrenin.',
      category: 'workflow',
      duration: 30,
      difficulty: 'advanced',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-11.jpg',
      author: 'Ahmet Yılmaz',
      views: 459,
      completed: false,
      new: true
    },
    {
      id: 12,
      title: 'Mobil Uygulama Kullanımı',
      description: 'Beetrax mobil uygulamasını etkin bir şekilde kullanmayı öğrenin.',
      category: 'mobile',
      duration: 14,
      difficulty: 'beginner',
      thumbnail: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-12.jpg',
      author: 'Zeynep Demir',
      views: 821,
      completed: false,
      new: false
    },
  ];

  const categories = [
    { id: 'all', name: 'Tümü', icon: <Book className="w-4 h-4" /> },
    { id: 'getting-started', name: 'Başlangıç', icon: <Play className="w-4 h-4" /> },
    { id: 'task-management', name: 'İş Takibi', icon: <Calendar className="w-4 h-4" /> },
    { id: 'stock-management', name: 'Stok Yönetimi', icon: <Package className="w-4 h-4" /> },
    { id: 'user-management', name: 'Kullanıcı Yönetimi', icon: <Users className="w-4 h-4" /> },
    { id: 'reporting', name: 'Raporlama', icon: <FileText className="w-4 h-4" /> },
    { id: 'api', name: 'API', icon: <Code className="w-4 h-4" /> },
    { id: 'workflow', name: 'İş Akışları', icon: <Settings className="w-4 h-4" /> },
    { id: 'mobile', name: 'Mobil', icon: <Settings className="w-4 h-4" /> },
  ];

  const difficulties = [
    { id: 'all', name: 'Tüm Seviyeler' },
    { id: 'beginner', name: 'Başlangıç' },
    { id: 'intermediate', name: 'Orta' },
    { id: 'advanced', name: 'İleri' },
  ];

  // Filter tutorials based on search query and category
  const filteredTutorials = tutorials.filter((tutorial) => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         tutorial.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filter === 'all' || tutorial.category === filter;
    const matchesDifficulty = difficulty === 'all' || tutorial.difficulty === difficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Play className={`w-6 h-6 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h1 className="text-xl font-bold">Beetrax Eğitimler</h1>
              </div>
            </div>
            
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Eğitim ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full py-2 pl-10 pr-4 rounded-lg ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                    : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } border focus:ring-2 focus:ring-blue-500/50 outline-none`}
              />
              <div className="absolute left-3 top-2.5">
                <Search className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className={`mb-8 p-8 rounded-2xl bg-gradient-to-r ${
          isDark ? 'from-blue-900/30 to-purple-900/30 border border-gray-700' : 'from-blue-50 to-purple-50 border border-gray-200'
        }`}>
          <div className="max-w-3xl">
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Beetrax'ı Keşfedin
            </h1>
            <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Adım adım eğitimler ve kapsamlı rehberler ile Beetrax'ın tüm özelliklerini öğrenin ve işletmenizin verimliliğini artırın.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/documentation"
                className={`inline-flex items-center px-4 py-2 rounded-lg font-medium ${
                  isDark 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Book className="w-4 h-4 mr-2" />
                Dokümantasyon
              </Link>
              
              <button 
                className={`inline-flex items-center px-4 py-2 rounded-lg font-medium ${
                  isDark 
                    ? 'bg-gray-700 text-white hover:bg-gray-600' 
                    : 'bg-white text-gray-800 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                <Play className="w-4 h-4 mr-2" />
                Eğitim Yolunuz
              </button>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Eğitim Videoları</h2>
            
            <div className={`flex gap-2 p-1 rounded-lg ${
              isDark ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              <select 
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded-lg ${
                  isDark 
                    ? 'bg-gray-700 text-white border-gray-600' 
                    : 'bg-white text-gray-800 border-gray-300'
                } border`}
              >
                {difficulties.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filter === category.id
                    ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                    : (isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Tutorials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTutorials.map((tutorial) => (
            <motion.div
              key={tutorial.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`overflow-hidden rounded-xl border ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } shadow-sm`}
            >
              <div className="relative">
                <img 
                  src={tutorial.thumbnail} 
                  alt={tutorial.title}
                  className="w-full h-40 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/tutorial-placeholder.jpg';
                  }}
                />
                <div className="absolute top-2 right-2 flex space-x-1">
                  {tutorial.new && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-blue-900/80 text-blue-300' : 'bg-blue-100 text-blue-800'
                    }`}>
                      Yeni
                    </span>
                  )}
                  {tutorial.completed && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-green-900/80 text-green-300' : 'bg-green-100 text-green-800'
                    }`}>
                      Tamamlandı
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <div className="flex items-center text-white text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>{tutorial.duration} dakika</span>
                    <div className={`ml-auto px-2 py-0.5 rounded-full text-xs ${
                      tutorial.difficulty === 'beginner' 
                        ? 'bg-green-700/70 text-green-100' 
                        : tutorial.difficulty === 'intermediate'
                          ? 'bg-yellow-700/70 text-yellow-100'
                          : 'bg-red-700/70 text-red-100'
                    }`}>
                      {tutorial.difficulty === 'beginner' ? 'Başlangıç' : 
                       tutorial.difficulty === 'intermediate' ? 'Orta' : 'İleri'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className={`font-semibold text-lg mb-2 line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {tutorial.title}
                </h3>
                <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {tutorial.description}
                </p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {tutorial.author}
                  </div>
                  <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <Star className="w-3 h-3 mr-1 fill-current text-yellow-400" />
                    <span>{tutorial.views.toLocaleString()} görüntüleme</span>
                  </div>
                </div>
                
                <button className={`w-full mt-4 py-2 px-4 rounded-lg flex items-center justify-center text-sm font-medium ${
                  isDark 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}>
                  <Play className="w-4 h-4 mr-2" />
                  İzle
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        
        {filteredTutorials.length === 0 && (
          <div className={`p-8 text-center rounded-xl ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <p className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Aramanızla eşleşen eğitim bulunamadı. Lütfen farklı bir arama terimi veya filtre deneyin.
            </p>
          </div>
        )}
        
        {/* Learning Paths */}
        <div className="mt-16">
          <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Öğrenme Yolları</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Beetrax Temel Eğitimi',
                description: 'Beetrax platformunun temellerini ve her gün kullanacağınız özellikleri öğrenin.',
                image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/path-1.jpg',
                modules: 5,
                duration: '2 saat'
              },
              {
                title: 'İş Takip Uzmanı Olun',
                description: 'Projelerinizi ve görevlerinizi profesyonelce yönetmeyi öğrenin.',
                image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/path-2.jpg',
                modules: 8,
                duration: '3.5 saat'
              },
              {
                title: 'Stok Yönetimi Uzmanı',
                description: 'Stok yönetimi sistemi ile envanterinizi etkili bir şekilde yönetmeyi öğrenin.',
                image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/path-3.jpg',
                modules: 7,
                duration: '3 saat'
              }
            ].map((path, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`overflow-hidden rounded-xl border ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } shadow-sm`}
              >
                <div className="relative">
                  <img 
                    src={path.image} 
                    alt={path.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/path-placeholder.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-xl font-bold text-white">{path.title}</h3>
                  </div>
                </div>
                
                <div className="p-4">
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {path.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Book className="w-4 h-4 mr-1" />
                      <span>{path.modules} modül</span>
                    </div>
                    <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{path.duration}</span>
                    </div>
                  </div>
                  
                  <button className={`w-full py-2 px-4 rounded-lg flex items-center justify-center text-sm font-medium ${
                    isDark 
                      ? 'bg-gray-700 text-white hover:bg-gray-600 border border-gray-600' 
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300'
                  }`}>
                    <span>Eğitime Başla</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Additional Resources */}
        <div className="mt-16 mb-8">
          <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Ek Kaynaklar</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            } shadow-sm`}>
              <div className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark ? 'bg-gray-700' : 'bg-blue-50'
              }`}>
                <Book className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Dokümantasyon</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Detaylı kullanım kılavuzları, referans belgeleri ve teknik dokümantasyona erişin.
              </p>
              <Link
                to="/documentation"
                className={`inline-flex items-center text-sm font-medium ${
                  isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                <span>Dokümantasyona Git</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            } shadow-sm`}>
              <div className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark ? 'bg-gray-700' : 'bg-blue-50'
              }`}>
                <FileText className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Şablonlar</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Hazır proje şablonları, görev listeleri ve iş akışı örneklerine göz atın.
              </p>
              <a
                href="#"
                className={`inline-flex items-center text-sm font-medium ${
                  isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                <span>Şablonları İncele</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            } shadow-sm`}>
              <div className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark ? 'bg-gray-700' : 'bg-blue-50'
              }`}>
                <MessageSquare className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Topluluk Forumu</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sorular sorun, çözümleri paylaşın ve diğer Beetrax kullanıcılarıyla bağlantı kurun.
              </p>
              <a
                href="#"
                className={`inline-flex items-center text-sm font-medium ${
                  isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                <span>Foruma Katılın</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-12 py-8 border-t ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                &copy; {new Date().getFullYear()} Beetrax. Tüm hakları saklıdır.
              </p>
            </div>
            
            <div className="flex space-x-6">
              <a 
                href="#" 
                onClick={handleHelpCenterClick}
                className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Yardım Merkezi
              </a>
              <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Gizlilik Politikası
              </a>
              <Link to="/blog" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Blog
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Authentication Alert */}
      {showAuthAlert && (
        <div className="fixed bottom-4 right-4 max-w-sm">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`p-4 rounded-lg shadow-lg flex items-start space-x-3 ${
              isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}
          >
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
            <div>
              <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Oturum Gerekli</h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Yardım merkezine erişmek için lütfen önce oturum açınız.
              </p>
              <div className="mt-2 flex space-x-2">
                <button 
                  onClick={() => navigate('/signin')}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Oturum Aç
                </button>
                <button 
                  onClick={() => setShowAuthAlert(false)}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Kapat
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Tutorials; 